import {
  type AxiosError,
  type TSupplier,
  type TSupplierFeature,
  getSeeriApi,
} from "@/api";
import { useCallback, useEffect, useState } from "react";
import {
  EditButton,
  EmailField,
  FunctionField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useNotify,
  useRecordContext,
  useUpdate,
  WithRecord,
} from "react-admin";
import { useAdmin } from "../../../hooks/admin";
import { SupplierAddressList } from "../addresses/list";
import { useParams } from "react-router-dom";
import { SUPPLIERS } from "../../../config/resources";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Switch,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AutoAwesome,
  BarChart,
  Campaign,
  ChatBubbleOutline,
  ContentCopy,
  CurrencyExchange,
  Mail,
  PhoneAndroid,
  SmartToy,
  Storefront,
  Timeline,
  CheckCircle,
  RadioButtonUnchecked,
  type SvgIconComponent,
} from "@mui/icons-material";
import { SupplierUsersList } from "./users";
import { country } from "@/config/country";
import { ModalImageField } from "@/components/elements/ModalImageField";
import { SupplierFeaturesField } from "@/components/suppliers/SupplierFeaturesField";
import { SupplierAgentConnectionsList } from "./agent-connections";
import { SupplierMarketingPricingTab } from "./marketing";
import { SupplierSettingsTab } from "./settings-tab";
import { SUPPLIER_FEATURES } from "@/constants/supplier-features";
import { SupplierDeleteButton } from "./SupplierDeleteButton";
import { SupplierEnsureAdminRoleButton } from "./SupplierEnsureAdminRoleButton";

const SupplierShowActions = () => {
  const { hasPermission } = useAdmin();
  const record = useRecordContext();
  // Sub-suppliers (con parentSupplierId) se usan para montar compras/órdenes SELL_IN: no se borran.
  const isSubSupplier = Boolean(record?.parentSupplierId);

  return (
    <TopToolbar>
      {hasPermission("supplier.update") && <SupplierEnsureAdminRoleButton />}
      {hasPermission("supplier.update") && <EditButton />}
      {hasPermission("supplier.delete") && !isSubSupplier && <SupplierDeleteButton />}
    </TopToolbar>
  );
};

export const SupplierShow = () => {
  const { hasPermission } = useAdmin();
  const { id } = useParams();

  if (!hasPermission("supplier.view")) return null;

  return (
    <Show actions={<SupplierShowActions />}>
      <TabbedShowLayout>
        <Tab label="Principal">
          <SupplierPrincipalTab />
        </Tab>
        <Tab label="Almacenes">
          <SupplierAddressList supplierId={id!} />
        </Tab>
        <Tab label="Usuarios">
          <SupplierUsersList supplierId={id!} />
        </Tab>
        <Tab label="Características">
          <SupplierFeaturesField />
        </Tab>
        <Tab label="Conexiones de Agente">
          <SupplierAgentConnectionsList supplierId={id!} />
        </Tab>
        <Tab label="Marketing">
          <SupplierMarketingPricingTab />
        </Tab>
        <Tab label="Analíticas">
          <AnalyticsWidgetSwitch />
          <AnalyticsSystemInstructionsEditor />
        </Tab>
        <Tab label="Configuración">
          <SupplierSettingsTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

// ---------------------------------------------------------------------------
// SupplierPrincipalTab — redesigned summary view
//
// Replaces the legacy single-column stack of Labeled+Field pairs with three
// composed sections:
//   • Identity header (logo, name, public name, status, copyable id, edit CTA)
//   • Contact + commercial info grid (email, channel, phone, currency, etc.)
//   • Capabilities grid (interactive feature toggles as scannable tiles)
//
// All field reads still flow through react-admin (TextField, EmailField,
// FunctionField) so RA's record context, translations, and i18n keep working.
// ---------------------------------------------------------------------------

const SupplierPrincipalTab = () => {
  return (
    <Stack spacing={3} sx={{ pt: 1 }}>
      <SupplierIdentityHeader />
      <SupplierContactCard />
      <SupplierCapabilitiesCard />
    </Stack>
  );
};

const SECTION_LABEL_SX = {
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontSize: "0.7rem",
  fontWeight: 600,
  color: "text.secondary",
} as const;

// --- Identity header --------------------------------------------------------

const SupplierIdentityHeader = () => {
  const record = useRecordContext<TSupplier>();
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const onCopyId = useCallback(() => {
    if (!record?.id) return;
    navigator.clipboard
      .writeText(record.id)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [record?.id]);

  if (!record) return null;
  const isActive = record.status !== false;

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderColor: "divider",
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 96,
              height: 96,
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ModalImageField source="logo" maxHeight={96} maxWidth={96} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                icon={<Storefront />}
                label="Distribuidor"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderColor: "divider",
                  "& .MuiChip-icon": { color: "primary.main" },
                }}
              />
              <Chip
                size="small"
                icon={isActive ? <CheckCircle /> : <RadioButtonUnchecked />}
                label={isActive ? "Activo" : "Inactivo"}
                color={isActive ? "success" : "default"}
                variant={isActive ? "filled" : "outlined"}
                sx={{ fontWeight: 600 }}
              />
              {(record as any).notificationOrderCreate && (
                <Chip
                  size="small"
                  variant="outlined"
                  label="Notifica pedidos"
                  sx={{ borderColor: "divider" }}
                />
              )}
              {(record as any).parentSupplierId && (
                <Chip
                  size="small"
                  color="warning"
                  variant="filled"
                  label="Sub-proveedor (pertenece a otro)"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>

            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {record.name || "Sin nombre"}
            </Typography>
            {(() => {
              const publicName = (record as any).publicName as
                | string
                | undefined;
              if (!publicName || publicName === record.name) return null;
              return (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25 }}
                >
                  {publicName}
                </Typography>
              );
            })()}

            <Tooltip title={copied ? "Copiado" : "Copiar ID"}>
              <Box
                onClick={onCopyId}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") onCopyId();
                }}
                sx={{
                  mt: 1.25,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: "pointer",
                  border: `1px dashed ${theme.palette.divider}`,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
                  fontSize: "0.78rem",
                  color: "text.secondary",
                  transition: "all 120ms ease",
                  "&:hover": {
                    color: "primary.main",
                    borderColor: "primary.main",
                  },
                }}
              >
                <Box component="span" sx={{ opacity: 0.7 }}>
                  ID
                </Box>
                <Box component="span">{record.id}</Box>
                <ContentCopy sx={{ fontSize: "0.85rem" }} />
              </Box>
            </Tooltip>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// --- Contact / commercial info ---------------------------------------------

type InfoFieldProps = {
  icon?: SvgIconComponent;
  label: string;
  children: React.ReactNode;
};
const InfoField = ({ icon: Icon, label, children }: InfoFieldProps) => (
  <Stack spacing={0.5}>
    <Stack direction="row" spacing={0.75} alignItems="center">
      {Icon && (
        <Icon
          sx={{ fontSize: "0.95rem", color: "text.secondary", opacity: 0.7 }}
        />
      )}
      <Typography sx={SECTION_LABEL_SX}>{label}</Typography>
    </Stack>
    <Box sx={{ pl: Icon ? 2.25 : 0, fontSize: "0.95rem", minHeight: 22 }}>
      {children}
    </Box>
  </Stack>
);

const SupplierContactCard = () => {
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Typography sx={{ ...SECTION_LABEL_SX, mb: 2 }}>
          Información general
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField icon={Mail} label="Correo">
              <EmailField source="email" emptyText="-" />
            </InfoField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField icon={ChatBubbleOutline} label="Canal de comunicaciones">
              <EmailField source="communicationChannel" emptyText="-" />
            </InfoField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField icon={PhoneAndroid} label="Teléfono / Celular">
              <WithRecord<TSupplier>
                render={(s) => {
                  const prefix = (s as any).phonePrefix as string | undefined;
                  const phone = s.phone as string | undefined;
                  if (!prefix && !phone) return <>-</>;
                  return (
                    <Box component="span">
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        {prefix || ""}
                      </Box>{" "}
                      {phone || "-"}
                    </Box>
                  );
                }}
              />
            </InfoField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField label="Nombre del contacto">
              <TextField source="contactName" emptyText="-" />
            </InfoField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField label="Razón social">
              <TextField source="socialReason" emptyText="-" />
            </InfoField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoField icon={CurrencyExchange} label="Moneda">
              <TextField source="currency" emptyText="-" />
            </InfoField>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// --- Capabilities grid ------------------------------------------------------

type SupplierToggleField =
  | "taniaEnabled"
  | "aiStartsConversation"
  | "showCurves"
  | "showBrandsBannerInCatalog";

type ToggleTileProps = {
  field: SupplierToggleField;
  icon: SvgIconComponent;
  title: string;
  description?: string;
  hidden?: (record: TSupplier) => boolean;
};

const ToggleTile = ({
  field,
  icon: Icon,
  title,
  description,
  hidden,
}: ToggleTileProps) => {
  const record = useRecordContext<TSupplier>();
  const notify = useNotify();
  const theme = useTheme();

  const value = Boolean(record?.[field]);

  const [update, { isLoading }] = useUpdate(
    SUPPLIERS,
    {
      id: record?.id,
      data: { ...record, [field]: !value },
      previousData: record,
    },
    {
      mutationMode: "pessimistic",
      onError: (error: AxiosError) =>
        notify(error.message ?? "Ocurrió un error", { type: "error" }),
    },
  );

  if (!record || (hidden && hidden(record))) return null;

  const accent = value ? theme.palette.primary.main : theme.palette.divider;

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderColor: value ? "primary.light" : "divider",
        transition: "border-color 140ms ease",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          backgroundColor: accent,
          transition: "background-color 140ms ease",
        }}
      />
      <CardContent sx={{ p: 2, pl: 2.5, "&:last-child": { pb: 2 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            variant="rounded"
            sx={{
              width: 32,
              height: 32,
              bgcolor: value ? "primary.main" : "action.hover",
              color: value ? "primary.contrastText" : "text.secondary",
              transition: "background-color 140ms ease",
            }}
          >
            <Icon sx={{ fontSize: "1.1rem" }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.25,
                color: "text.primary",
              }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.25,
                  lineHeight: 1.4,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
          {isLoading ? (
            <CircularProgress size={20} sx={{ mt: 0.75, mr: 0.75 }} />
          ) : (
            <Switch
              size="small"
              checked={value}
              onChange={() => update()}
              onClick={(event) => event.stopPropagation()}
              inputProps={{ "aria-label": title }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const SupplierCapabilitiesCard = () => {
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography sx={SECTION_LABEL_SX}>Capacidades del proveedor</Typography>
          <WithRecord<TSupplier>
            render={(s) => {
              const enabled = [
                s.taniaEnabled,
                s.taniaEnabled && s.aiStartsConversation,
                s.showCurves,
                s.showBrandsBannerInCatalog,
              ].filter(Boolean).length;
              const total = s.taniaEnabled ? 4 : 3;
              return (
                <Chip
                  size="small"
                  label={`${enabled} de ${total} activas`}
                  sx={{ fontWeight: 600 }}
                />
              );
            }}
          />
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <ToggleTile
              field="taniaEnabled"
              icon={SmartToy}
              title="Habilitar Tania"
              description="Activa el agente Tania en las conversaciones del proveedor."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <ToggleTile
              field="aiStartsConversation"
              icon={AutoAwesome}
              title="IA en conversaciones"
              description="La IA toma la iniciativa al iniciar conversaciones nuevas."
              hidden={(s) => !s.taniaEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <ToggleTile
              field="showCurves"
              icon={Timeline}
              title="Habilitar curvas"
              description="Muestra la opción de curvas en los pedidos."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <ToggleTile
              field="showBrandsBannerInCatalog"
              icon={Campaign}
              title="Banner de marcas en catálogo"
              description="Renderiza un banner con las marcas destacadas en el catálogo."
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Legacy single-switch components kept around so other tabs (Analíticas)
// can reuse them — refactored to drive through the same Switch component
// for visual consistency.
const AiStartsConversationSwitch = () => {
  const record = useRecordContext<TSupplier>();
  if (!record?.taniaEnabled) return null;
  return (
    <Box sx={{ maxWidth: 480 }}>
      <ToggleTile
        field="aiStartsConversation"
        icon={AutoAwesome}
        title="IA en conversaciones"
      />
    </Box>
  );
};











const ANALYTICS_WIDGET_KEY = "ai.analytics.widget";

function isFeatureEnabledInTree(
  features: TSupplierFeature[] | undefined,
  key: string
): boolean {
  if (!features) return false;
  const search = (
    items: TSupplierFeature[],
    k: string
  ): boolean | undefined => {
    for (const f of items) {
      if (f.key === k) return f.enabled === true;
      if (f.children) {
        const found = search(f.children, k);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };
  return search(features, key) ?? false;
}

function toggleFeatureInTree(
  features: TSupplierFeature[] | undefined,
  key: string,
  enabled: boolean
): TSupplierFeature[] {
  if (!features) return [];
  return features.map((f) => ({
    ...f,
    enabled: f.key === key ? enabled : f.enabled,
    children: f.children
      ? toggleFeatureInTree(f.children, key, enabled)
      : undefined,
  }));
}

const AnalyticsWidgetSwitch = () => {
  const record = useRecordContext<TSupplier>();
  const notify = useNotify();

  const currentlyEnabled = isFeatureEnabledInTree(
    record?.features,
    ANALYTICS_WIDGET_KEY
  );

  const featuresBase =
    record?.features && record.features.length > 0
      ? record.features
      : structuredClone(SUPPLIER_FEATURES);

  const [update, { isLoading }] = useUpdate(
    SUPPLIERS,
    {
      id: record?.id,
      data: {
        features: toggleFeatureInTree(
          featuresBase,
          ANALYTICS_WIDGET_KEY,
          !currentlyEnabled
        ),
      },
      previousData: record,
    },
    {
      mutationMode: "pessimistic",
      onError: (error: AxiosError | Error) => {
        const ax = error as AxiosError<{ message?: string }>;
        const msg =
          ax.response?.data?.message ??
          (error instanceof Error ? error.message : null) ??
          "Ocurrió un error";
        notify(msg, { type: "error" });
      },
    }
  );

  return (
    <Box sx={{ maxWidth: 480, mb: 2 }}>
      <Card variant="outlined">
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                width: 32,
                height: 32,
                bgcolor: currentlyEnabled ? "primary.main" : "action.hover",
                color: currentlyEnabled
                  ? "primary.contrastText"
                  : "text.secondary",
              }}
            >
              <BarChart sx={{ fontSize: "1.1rem" }} />
            </Avatar>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
              Habilitar widget de analíticas
            </Typography>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Switch
                size="small"
                checked={currentlyEnabled}
                onChange={() => update()}
                inputProps={{ "aria-label": "Habilitar widget de analíticas" }}
              />
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const AnalyticsSystemInstructionsEditor = () => {
  const record = useRecordContext<TSupplier>();
  const notify = useNotify();
  const [instructions, setInstructions] = useState("");
  const [originalInstructions, setOriginalInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!record?.id) return;
    getSeeriApi()
      .get(`/api/analytics-agent/api/analytics/v1/config?supplierId=${record.id}`)
      .then((res) => {
        const val = res.data?.systemInstructions || "";
        setInstructions(val);
        setOriginalInstructions(val);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [record?.id]);

  const handleSave = useCallback(async () => {
    if (!record?.id) return;
    setSaving(true);
    try {
      await getSeeriApi().put(
        "/api/analytics-agent/api/analytics/v1/config",
        { supplierId: record.id, systemInstructions: instructions }
      );
      setOriginalInstructions(instructions);
      notify("Instrucciones guardadas correctamente", { type: "success" });
    } catch (error: any) {
      const msg =
        error.response?.data?.error ?? error.message ?? "Error al guardar";
      notify(msg, { type: "error" });
    } finally {
      setSaving(false);
    }
  }, [record?.id, instructions, notify]);

  if (!loaded) return null;

  const hasChanges = instructions !== originalInstructions;

  return (
    <Box sx={{ mt: 2, maxWidth: 800 }}>
      <Typography variant="subtitle1" gutterBottom>
        Instrucciones del sistema
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Personaliza las instrucciones del agente de analítica para este
        proveedor. Dejar vacío usa las instrucciones por defecto.
      </Typography>
      <MuiTextField
        multiline
        minRows={8}
        maxRows={20}
        fullWidth
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        placeholder="Dejar vacío para usar instrucciones por defecto..."
        variant="outlined"
        size="small"
      />
      <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? "Guardando..." : "Guardar instrucciones"}
        </Button>
        {hasChanges && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setInstructions(originalInstructions)}
          >
            Descartar cambios
          </Button>
        )}
      </Box>
    </Box>
  );
};

